import React, { useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import type { UsePaginationReturn } from '../../core/usePagination';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SIBLINGS = 1;

// ─── Tokens de tamaño ────────────────────────────────────────────────────────
const SIZE_TOKENS = {
  // auto → sm por debajo de 1536 px (laptops) y md desde 1536 px.
  // Es lo que se usa cuando no se indica `size`.
  auto: {
    btn:         'w-6 h-6 2xl:w-7 2xl:h-7',
    icon:        'w-3 h-3 2xl:w-3.5 2xl:h-3.5',
    selectH:     'h-6 2xl:h-7',
    containerPy: 'py-2 2xl:py-2.5',
    font:        'text-[11px] 2xl:text-xs',
  },
  sm: {
    btn:         'w-6 h-6',       // 24px
    icon:        'w-3 h-3',
    selectH:     'h-6',
    containerPy: 'py-2',
    font:        'text-[11px]',
  },
  md: {
    btn:         'w-7 h-7',       // 28px — recomendado
    icon:        'w-3.5 h-3.5',
    selectH:     'h-7',
    containerPy: 'py-2.5',
    font:        'text-xs',       // 12px
  },
} as const;

export type PaginacionSize = Exclude<keyof typeof SIZE_TOKENS, 'auto'>;

export interface PaginacionProps extends UsePaginationReturn {
  /**
   * Tamaño del componente
   * - sm: 24px botones — compacto, para espacios reducidos
   * - md: 28px botones — recomendado para tablas de gestión/backoffice
   * @default 'md'
   */
  size?: PaginacionSize;
  /**
   * Mostrar selector de tamaño de página
   * @default true
   */
  showPageSizeSelector?: boolean;
  /**
   * Mostrar info "X–Y de Z registros"
   * @default true
   */
  showInfo?: boolean;
  /**
   * Mostrar botones primera/última página («»)
   * @default true
   */
  showEdgeButtons?: boolean;
  /**
   * Label del contador (ej: "clientes", "facturas")
   * @default "registros"
   */
  itemLabel?: string;
  /**
   * Tamaños de página disponibles
   * @default [10, 20, 50, 100]
   */
  pageSizeOptions?: number[];
  /**
   * Clases extra para el contenedor raíz
   */
  className?: string;
}

export const Paginacion: React.FC<PaginacionProps> = ({
  page,
  totalPages,
  pageSize,
  total,
  from,
  to,
  setPage,
  prevPage,
  nextPage,
  firstPage,
  lastPage,
  hasPrev,
  hasNext,
  setPageSize,
  size,
  showPageSizeSelector = true,
  showInfo = true,
  showEdgeButtons = true,
  itemLabel = 'registros',
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  className = '',
}) => {
  // Sin `size` explícito, el tamaño se adapta a la pantalla (ver SIZE_TOKENS.auto)
  const t = SIZE_TOKENS[size ?? 'auto'];

  // ── Generar lista de páginas con "..." ──────────────────────────────────────
  const pageNumbers = useMemo(() => {
    const totalNums = SIBLINGS * 2 + 5;

    if (totalPages <= totalNums) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const leftSibling  = Math.max(page - SIBLINGS, 1);
    const rightSibling = Math.min(page + SIBLINGS, totalPages);
    const showLeftDots  = leftSibling > 2;
    const showRightDots = rightSibling < totalPages - 1;

    if (!showLeftDots && showRightDots) {
      const leftCount = 3 + SIBLINGS * 2;
      return [
        ...Array.from({ length: leftCount }, (_, i) => i + 1),
        '...',
        totalPages,
      ];
    }

    if (showLeftDots && !showRightDots) {
      const rightCount = 3 + SIBLINGS * 2;
      return [
        1,
        '...',
        ...Array.from({ length: rightCount }, (_, i) => totalPages - rightCount + i + 1),
      ];
    }

    return [
      1,
      '...',
      ...Array.from({ length: SIBLINGS * 2 + 1 }, (_, i) => leftSibling + i),
      '...',
      totalPages,
    ];
  }, [page, totalPages]);

  if (total === 0) return null;

  return (
    <div
      className={'flex items-center justify-between gap-3 px-4 border-t ' + t.containerPy + (className ? ' ' + className : '')}
      style={{
        borderColor: 'var(--border-default)',
        background:  'var(--surface-0)',
      }}
    >
      {/* ── Izquierda: info + selector de filas ──────────────────────────── */}
      <div className="flex items-center gap-3">
        {showInfo && (
          <span
            className={'font-mono whitespace-nowrap ' + t.font}
            style={{ color: 'var(--text-tertiary)' }}
          >
            {from + 1}–{to + 1} de {total.toLocaleString()} {itemLabel}
          </span>
        )}

        {showPageSizeSelector && (
          <div className="flex items-center gap-1.5">
            <span
              className={'font-mono whitespace-nowrap ' + t.font}
              style={{ color: 'var(--text-tertiary)' }}
            >
              Filas:
            </span>
            <select
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              className={'rounded font-mono px-1 cursor-pointer focus:outline-none ' + t.selectH + ' ' + t.font}
              style={{
                border:      '1px solid var(--border-strong)',
                color:       'var(--text-secondary)',
                background:  'var(--surface-0)',
              }}
            >
              {pageSizeOptions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* ── Derecha: controles de navegación ─────────────────────────────── */}
      <div className="flex items-center gap-0.5">

        {/* « Primera página */}
        {showEdgeButtons && (
          <NavBtn
            btnClass={t.btn}
            onClick={firstPage}
            disabled={!hasPrev}
            aria-label="Primera página"
          >
            <ChevronsLeft className={t.icon} />
          </NavBtn>
        )}

        {/* ‹ Anterior */}
        <NavBtn
          btnClass={t.btn}
          onClick={prevPage}
          disabled={!hasPrev}
          aria-label="Página anterior"
        >
          <ChevronLeft className={t.icon} />
        </NavBtn>

        {/* Números de página */}
        {pageNumbers.map((num, idx) =>
          num === '...'
            ? (
              <span
                key={'dots-' + idx}
                className={'flex items-center justify-center select-none ' + t.btn + ' ' + t.font}
                style={{ color: 'var(--text-tertiary)' }}
              >
                …
              </span>
            )
            : (
              <PageNumBtn
                key={num}
                btnClass={t.btn}
                fontClass={t.font}
                active={page === num}
                onClick={() => setPage(num as number)}
                aria-label={'Página ' + num}
                aria-current={page === num ? 'page' : undefined}
              >
                {num}
              </PageNumBtn>
            )
        )}

        {/* › Siguiente */}
        <NavBtn
          btnClass={t.btn}
          onClick={nextPage}
          disabled={!hasNext}
          aria-label="Página siguiente"
        >
          <ChevronRight className={t.icon} />
        </NavBtn>

        {/* » Última página */}
        {showEdgeButtons && (
          <NavBtn
            btnClass={t.btn}
            onClick={lastPage}
            disabled={!hasNext}
            aria-label="Última página"
          >
            <ChevronsRight className={t.icon} />
          </NavBtn>
        )}
      </div>
    </div>
  );
};

// ─── Botón de navegación (flechas) ───────────────────────────────────────────
interface NavBtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  btnClass: string;
  children: React.ReactNode;
}

const NavBtn: React.FC<NavBtnProps> = ({ btnClass, disabled, children, ...props }) => {
  const borderColor = disabled ? 'var(--border-muted)' : 'var(--border-default)';
  const textColor = disabled ? 'var(--text-disabled)' : 'var(--text-tertiary)';

  return (
    <button
      disabled={disabled}
      className={'flex items-center justify-center rounded transition-colors duration-100 focus:outline-none ' + btnClass}
      style={{
        border:     '1px solid ' + borderColor,
        color:      textColor,
        background: 'var(--surface-0)',
        cursor:     disabled ? 'not-allowed' : 'pointer',
        opacity:    disabled ? 0.5 : 1,
      }}
      {...props}
    >
      {children}
    </button>
  );
};

// ─── Botón de número de página ────────────────────────────────────────────────
interface PageNumBtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  btnClass: string;
  fontClass: string;
  active?: boolean;
  children: React.ReactNode;
}

const PageNumBtn: React.FC<PageNumBtnProps> = ({
  btnClass,
  fontClass,
  active,
  children,
  ...props
}) => {
  const styleConfig = active
    ? {
        border:     '1px solid var(--border-focus)',
        color:      'var(--primary)',
        background: 'var(--surface-0)',
        fontWeight: 600 as const,
      }
    : {
        border:     '1px solid var(--border-default)',
        color:      'var(--text-tertiary)',
        background: 'var(--surface-0)',
      };

  return (
    <button
      className={'flex items-center justify-center rounded font-mono transition-colors duration-100 focus:outline-none cursor-pointer ' + btnClass + ' ' + fontClass}
      style={styleConfig}
      {...props}
    >
      {children}
    </button>
  );
};

Paginacion.displayName = 'Paginacion';


